import { useState, type FormEvent } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import { formatUsPhoneDisplay, normalizeWebsiteUrl, usPhoneDigitsFromInput } from '../../../../shared/formatting';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';
import type { StepProps } from '../AdminSignUpPage';

const MAX_LOGO_BYTES = 10 * 1024 * 1024;

function identificationDigitsFromInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 9);
}

/** EIN displays XX-XXXXXXX, SSN displays XXX-XX-XXXX — both share the same 9-digit canonical value, just
 * grouped differently; see Company.IdentificationType/IdentificationLast4 on the backend, which only ever
 * keeps the last 4 of whichever format was entered. */
function formatIdentificationDisplay(digits: string, type: 'Ssn' | 'Ein' | ''): string {
  if (type === 'Ein') {
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  if (type === 'Ssn') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  return digits;
}

/**
 * Step 2 — Partner Information (Partner Sign Up only; replaces Admin Sign Up's Company Information step).
 * Submitting this step calls the same anonymous `POST /api/registrations` Admin Sign Up uses, with
 * `channel: "Partner"` plus the three Partner-only optional fields (identification type/number, fax
 * number) — see RegistrationContracts.CreateRegistrationRequest's own doc comment. The full identification
 * number is sent once, over the same onboarding-session-establishing call, and is never stored in full
 * server-side (only its last 4 digits — see Company.IdentificationLast4); it is never logged or re-displayed
 * by this screen either.
 * <p>
 * Deliberately leaner than Admin Sign Up's Company Information screen: no FEIN/SEIN/Employee Count prompts.
 * Those fields remain nullable on Company and can be filled in later from Settings — a partner firm
 * registering itself isn't necessarily its own payroll-tax-registered employer at this stage, and inventing
 * required values for fields the approved design doesn't ask for here would be worse than leaving them
 * blank. See docs/epn-35-partner-signup-verification-gaps.md.
 */
export function PartnerInfoStep({ goNext, goBack }: StepProps) {
  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [faxNumber, setFaxNumber] = useState('');
  const [identificationType, setIdentificationType] = useState<'Ssn' | 'Ein' | ''>('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleLogoSelected(file: File) {
    setError(null);
    if (file.size > MAX_LOGO_BYTES) {
      setError(`${file.name} exceeds the 10 MB limit.`);
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError(`${file.name} is not a supported format (PNG, JPG).`);
      return;
    }
    setLogoFile(file);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await adminSignupGateway.createRegistration({
        companyName: businessName,
        ein: null,
        sein: null,
        website: normalizeWebsiteUrl(website) || null,
        contactEmail: contactEmail || null,
        contactPhone: contactPhone || null,
        employeeCount: null,
        channel: 'Partner',
        identificationType: identificationType || null,
        identificationNumber: identificationNumber || null,
        faxNumber: faxNumber || null,
      });

      if (logoFile) {
        await adminSignupGateway.uploadLogo(logoFile);
      }

      goNext();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save partner information'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="screen active" data-testid="partner-info-step">
      <div className="screen-icon" aria-hidden="true">
        🤝
      </div>
      <h2>Step 2: Partner Information</h2>
      <div className="substepper" aria-hidden="true">
        <div className="substep active">
          <span className="dot">1</span>Partner Info
        </div>
        <span className="subline" />
        <div className="substep">
          <span className="dot">2</span>Identity Verification
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field-grid">
          <div className="field full">
            <label htmlFor="businessName">
              Business Name <span className="req">*</span>
            </label>
            <input className="control" id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required autoFocus />
          </div>

          <div className="field full">
            <label htmlFor="partnerWebsite">Business Website</label>
            <input
              className="control"
              id="partnerWebsite"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onBlur={(e) => setWebsite(normalizeWebsiteUrl(e.target.value))}
              placeholder="https://www.company.com"
            />
          </div>

          <div className="field">
            <label htmlFor="partnerContactEmail">Contact Email</label>
            <input
              className="control"
              id="partnerContactEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
            />
          </div>

          <div className="field">
            <label htmlFor="partnerContactPhone">Contact Phone Number</label>
            <input
              className="control"
              id="partnerContactPhone"
              inputMode="tel"
              value={formatUsPhoneDisplay(contactPhone)}
              onChange={(e) => setContactPhone(usPhoneDigitsFromInput(e.target.value))}
              placeholder="(XXX) XXX-XXXX"
              autoComplete="tel"
              maxLength={14}
            />
          </div>

          <div className="field">
            <label htmlFor="partnerFaxNumber">Fax Number</label>
            <input
              className="control"
              id="partnerFaxNumber"
              inputMode="tel"
              value={formatUsPhoneDisplay(faxNumber)}
              onChange={(e) => setFaxNumber(usPhoneDigitsFromInput(e.target.value))}
              placeholder="(XXX) XXX-XXXX"
              maxLength={14}
            />
          </div>

          <div className="field">
            <label htmlFor="identificationType">Identification Type</label>
            <select
              className="control"
              id="identificationType"
              value={identificationType}
              onChange={(e) => {
                setIdentificationType(e.target.value as 'Ssn' | 'Ein' | '');
                setIdentificationNumber('');
              }}
            >
              <option value="">Select</option>
              <option value="Ein">EIN</option>
              <option value="Ssn">SSN</option>
            </select>
          </div>

          {identificationType !== '' && (
            <div className="field">
              <label htmlFor="identificationNumber">{identificationType === 'Ein' ? 'EIN' : 'SSN'}</label>
              <input
                className="control"
                id="identificationNumber"
                inputMode="numeric"
                autoComplete="off"
                value={formatIdentificationDisplay(identificationNumber, identificationType)}
                onChange={(e) => setIdentificationNumber(identificationDigitsFromInput(e.target.value))}
                placeholder={identificationType === 'Ein' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX'}
                maxLength={11}
              />
            </div>
          )}

          <div className="field full">
            <label htmlFor="partnerLogo">Business Logo</label>
            <input
              type="file"
              id="partnerLogo"
              accept="image/png,image/jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleLogoSelected(file);
                e.target.value = '';
              }}
            />
            {logoFile && <p className="info-line" style={{ margin: '6px 0 0' }}>Selected: {logoFile.name}</p>}
          </div>
        </div>

        {error && (
          <p className="error-line" role="alert">
            <span className="info-icon">i</span>
            <span>{error}</span>
          </p>
        )}

        <div className="actions">
          <button className="btn btn-back" type="button" onClick={goBack}>
            Go Back
          </button>
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
